// Shared on-demand "advanced analysis" (sandbox detonation) logic + UI.
// Used by both the standard (NAI) and AI phishing analysis pages so the
// behavior stays identical. Detonates attachments AND URLs in Hybrid
// Analysis / ANY.RUN, polls for results, and exposes a malicious flag the
// host page uses to escalate its verdict.

import React, { useEffect, useRef, useState } from "react";

const fmtVerdict = (v) => {
  const s = String(v || "").toLowerCase();
  if (s === "malicious") return "Malicious";
  if (s === "suspicious") return "Suspicious";
  if (s === "clean") return "Clean";
  if (s === "pending") return "Pending";
  return "Unknown";
};

// Verdict taxonomy ordering (matches the server). Used to fold the sandbox
// result into the overall verdict via the same matrix-MAX rule as the other
// engines.
const VERDICT_ORDER = { Clean: 0, "Needs Review": 1, Spam: 2, Malicious: 3 };

// Treat the Advanced Sandbox detonation as a THIRD engine and combine it with
// the already-computed (AI + Detection Engine) verdict, never downgrading:
//   malicious detonation  → Malicious   (hard-confirmed technical proof)
//   suspicious detonation → Needs Review (route to an analyst)
// Returns { verdict, sandboxVerdict, raisedBySandbox }.
export function foldSandboxVerdict(baseVerdict, { detoMalicious, detoSuspicious } = {}) {
  const sandboxVerdict = detoMalicious ? "Malicious" : detoSuspicious ? "Needs Review" : null;
  if (!sandboxVerdict) return { verdict: baseVerdict, sandboxVerdict: null, raisedBySandbox: false };
  const baseRank = VERDICT_ORDER[baseVerdict] ?? -1;
  const raised = VERDICT_ORDER[sandboxVerdict] > baseRank;
  return { verdict: raised ? sandboxVerdict : baseVerdict, sandboxVerdict, raisedBySandbox: raised };
}

const statusLabel = (p) => {
  if (!p) return "—";
  if (p.status === "completed") return fmtVerdict(p.verdict || "unknown");
  if (p.status === "submitted" || p.status === "running") return "Detonating…";
  if (p.status === "not_configured") return "not configured";
  if (p.status === "error") return "error";
  return p.status;
};

// Pastel pill palettes — readable on both light and dark cards, so they
// don't depend on theme-specific CSS (which was hiding them in light mode).
const PALETTE = {
  malicious: { bg: "#fde7e9", fg: "#a4262c", bd: "#f1707b" },
  suspicious: { bg: "#fff4ce", fg: "#8a4b00", bd: "#ffd335" },
  clean: { bg: "#dff6dd", fg: "#0e700e", bd: "#9fd89f" },
  neutral: { bg: "#eef1f6", fg: "#475569", bd: "#cbd5e1" },
  error: { bg: "#fde7e9", fg: "#a4262c", bd: "#f1707b" },
};

const pillPalette = (p) => {
  if (!p) return PALETTE.neutral;
  if (p.status === "completed") return PALETTE[p.verdict] || PALETTE.neutral;
  if (p.status === "submitted" || p.status === "running") return PALETTE.suspicious;
  if (p.status === "error" || p.status === "not_configured") return PALETTE.error;
  return PALETTE.neutral;
};

const pillStyle = (p) => {
  const c = pillPalette(p);
  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: "0.78rem",
    fontWeight: 600,
    background: c.bg,
    color: c.fg,
    border: `1px solid ${c.bd}`,
    textDecoration: "none",
    whiteSpace: "nowrap",
  };
};

const badgeStyle = (kind) => {
  const c = kind === "url" ? { bg: "#eff6ff", fg: "#1d4ed8", bd: "#93c5fd" } : { bg: "#ecfdf5", fg: "#15803d", bd: "#86efac" };
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    background: c.bg,
    color: c.fg,
    border: `1px solid ${c.bd}`,
    marginRight: 8,
  };
};

/**
 * @param {Object}   opts
 * @param {File}     opts.file      the original uploaded email (for re-submit)
 * @param {Object}   opts.result    the analysis result object
 * @param {string}   opts.endpoint  advanced-analysis POST endpoint for this page
 */
const getToken = () => sessionStorage.getItem("auth_token") || "";
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

export function useAdvancedAnalysis({ file, result, endpoint }) {
  const [providers, setProviders] = useState([]);
  const [defaultProvider, setDefaultProvider] = useState(null);
  // Admin toggle: when true, hide Advanced Analysis once both engines confirm
  // Malicious. Init true to match the server default (avoids a hide→show flash).
  const [suppressWhenMalicious, setSuppressWhenMalicious] = useState(true);
  const [analysisId, setAnalysisId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [choice, setChoice] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [deto, setDeto] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const fileName = result?.fileName || null;
  const attachmentsCount = Array.isArray(result?.attachments) ? result.attachments.length : 0;
  const urlsCount = Array.isArray(result?.urls) ? result.urls.length : 0;

  // Discover available sandbox providers when a fresh result arrives; reset prior run.
  useEffect(() => {
    setAnalysisId(null);
    setDeto(null);
    setError("");
    setModalOpen(false);
    clearTimer();
    if (!fileName) {
      setProviders([]);
      setDefaultProvider(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/detonations/providers", { headers: authHeader() });
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        setProviders(json.providers || []);
        setDefaultProvider(json.default || null);
        setSuppressWhenMalicious(json.suppressAdvancedWhenMalicious !== false);
      } catch {
        /* ignore — button just won't show */
      }
    })();
    return () => { cancelled = true; };
  }, [fileName]);

  // Poll detonation status once started.
  useEffect(() => {
    clearTimer();
    if (!analysisId) return;
    let stopped = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 120; // ~10 min @ 5s
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`/api/detonations/${analysisId}`, { headers: authHeader() });
        if (res.ok) {
          const json = await res.json();
          if (!stopped) setDeto(json);
          if (json.status === "complete" || attempts >= MAX_ATTEMPTS) clearTimer();
        }
      } catch {
        /* transient */
      }
    };
    poll();
    timerRef.current = setInterval(poll, 5000);
    return () => { stopped = true; clearTimer(); };
  }, [analysisId]);

  const startAdvanced = async (provider, { setAsDefault: sd = false } = {}) => {
    if (!file) {
      setError("The original email file is no longer available. Re-run the analysis first.");
      return;
    }
    setError("");
    setStarting(true);
    try {
      if (sd && provider) {
        try {
          await fetch("/api/detonations/default", {
            method: "POST",
            headers: { ...authHeader(), "Content-Type": "application/json" },
            body: JSON.stringify({ provider }),
          });
          setDefaultProvider(provider);
        } catch { /* non-fatal */ }
      }
      const fd = new FormData();
      fd.append("file", file);
      if (provider) fd.append("provider", provider);
      const res = await fetch(endpoint, { method: "POST", headers: authHeader(), body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setDeto(json);
      if (!json.enabled || !json.items?.length) {
        setError("Nothing was eligible for detonation in this email.");
        return;
      }
      setAnalysisId(json.analysisId);
      setModalOpen(false);
    } catch (e) {
      setError(e.message || "Failed to start advanced analysis.");
    } finally {
      setStarting(false);
    }
  };

  const onPerform = () => {
    setError("");
    if (providers.length === 0) return;
    if (providers.length === 1) return startAdvanced(providers[0].key);
    if (defaultProvider) return startAdvanced(defaultProvider);
    setChoice(providers[0]?.key || null);
    setSetAsDefault(false);
    setModalOpen(true);
  };

  const canAdvanced = providers.length > 0 && (attachmentsCount > 0 || urlsCount > 0);
  const advStarted = !!analysisId || !!deto;

  // When both engines (AI + Detection Engine) have already confirmed the email
  // as Malicious, the verdict is settled — a sandbox detonation can only escalate
  // to Malicious, so it adds nothing. If the admin has enabled the toggle, hide
  // the whole Advanced Analysis feature (entry-point button + tab + card) in that
  // case. (Strict "malicious" only — "potentially malicious" is not a
  // confirmation.) Still surface it once a run has started so its results remain
  // reachable.
  const isConfirmedMalicious = (v) => {
    const s = String(v || "").toLowerCase();
    return s.includes("malicious") && !s.includes("potentially");
  };
  const aiConfirmedMalicious = isConfirmedMalicious(result?.aiRawVerdict || result?.aiVerdict);
  const engineConfirmedMalicious = isConfirmedMalicious(result?.heuristicVerdict || result?.verdict);
  const verdictConfirmedMalicious =
    suppressWhenMalicious && aiConfirmedMalicious && engineConfirmedMalicious;
  const showAdvanced = canAdvanced && (advStarted || !verdictConfirmedMalicious);
  const detoMalicious = !!deto?.malicious;
  const detoSuspicious = !detoMalicious && !!deto?.suspicious;
  // The sandbox's aggregate verdict ("malicious" | "suspicious" | "clean" |
  // "pending" | "unknown") and whether it has finished — consumed by the
  // result views to fold the sandbox into the overall verdict as a 3rd engine.
  const detoVerdict = deto?.verdict || null;
  const detoComplete = deto?.status === "complete";

  // Simulated progress to keep the user engaged while the sandbox runs
  // (sandboxes don't expose a percentage). Ramps to ~90% while pending,
  // snaps to 100% on completion.
  const [progress, setProgress] = useState(0);
  const detoStatus = deto?.status;
  useEffect(() => {
    if (!advStarted) { setProgress(0); return; }
    if (detoStatus === "complete") { setProgress(100); return; }
    setProgress((p) => (p < 12 ? 12 : p));
    const id = setInterval(() => setProgress((p) => Math.min(90, p + 3)), 1500);
    return () => clearInterval(id);
  }, [advStarted, detoStatus]);

  return {
    providers, defaultProvider, deto, error, starting,
    canAdvanced, showAdvanced, advStarted, detoMalicious, detoSuspicious, detoVerdict, detoComplete, progress,
    modalOpen, setModalOpen, choice, setChoice, setAsDefault, setSetAsDefault,
    onPerform, startAdvanced,
  };
}

/* ---------------- Top button (next to the verdict) ---------------- */
export function AdvancedAnalysisButton({ adv, onStart }) {
  if (!adv.showAdvanced) return null;
  const label = adv.starting
    ? "Starting…"
    : adv.advStarted
    ? "View Advanced Analysis"
    : "Perform Advanced Analysis";
  return (
    <button
      type="button"
      className="analyze-btn"
      style={{ whiteSpace: "nowrap" }}
      disabled={adv.starting}
      onClick={() => {
        if (!adv.advStarted) adv.onPerform();
        onStart && onStart();
        // Bring the results panel into view (it renders below the fold).
        setTimeout(() => {
          document
            .getElementById("pm-advanced-analysis")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      }}
      title="Detonate / look up this email's attachments and URLs in an external sandbox"
    >
      {label}
    </button>
  );
}

/* ---------------- Card (tab content: results) ---------------- */
export function AdvancedAnalysisCard({ adv }) {
  if (!adv.showAdvanced) {
    return (
      <div className="results-grid">
        <div className="column">
          <div className="card">
            <div className="card-header"><h3>Advanced Sandbox Analysis</h3></div>
            <div className="placeholder-row" style={{ padding: "14px 16px" }}>
              Advanced analysis isn't available for this email — it requires a
              configured sandbox API key and at least one attachment or URL.
            </div>
          </div>
        </div>
      </div>
    );
  }
  const items = adv.deto?.items || [];
  const cols = adv.providers; // [{key,label}]
  const allError =
    items.length > 0 &&
    items.every((it) =>
      Object.values(it.providers || {}).every(
        (p) => p.status === "error" || p.status === "not_configured"
      )
    );

  const cell = (p) => {
    if (!p) return <span className="muted">—</span>;
    const pill = p.reportLink ? (
      <a href={p.reportLink} target="_blank" rel="noreferrer" style={pillStyle(p)} title="View sandbox report">
        {statusLabel(p)}
      </a>
    ) : (
      <span style={pillStyle(p)}>{statusLabel(p)}</span>
    );
    return (
      <div>
        {pill}
        {p.mode === "lookup" && p.status === "completed" && (
          <div style={{ marginTop: 4, fontSize: "0.72rem", color: "#64748b" }}>
            from existing report (no live detonation)
          </div>
        )}
        {p.error && (
          <div style={{ marginTop: 4, fontSize: "0.72rem", color: "#a4262c", maxWidth: 320, wordBreak: "break-word" }}>
            {p.error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="results-grid" id="pm-advanced-analysis">
      <div className="column">
        <div className="card">
          <div className="card-header">
            <h3>Advanced Sandbox Analysis</h3>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              {cols.map((c) => c.label).join(" · ")}
              {adv.advStarted && adv.deto?.status === "pending" && " · detonating…"}
            </span>
          </div>

          {adv.advStarted && (
            <div style={{ padding: "12px 16px 0" }}>
              <div style={{ height: 8, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${adv.progress}%`,
                    background:
                      adv.deto?.status === "complete"
                        ? adv.detoMalicious
                          ? "#dc2626"
                          : allError
                          ? "#9ca3af"
                          : "#16a34a"
                        : "#6e2dd1",
                    transition: "width 0.6s ease",
                  }}
                />
              </div>
              <div className="muted" style={{ fontSize: "0.75rem", marginTop: 6 }}>
                {adv.deto?.status === "complete"
                  ? allError
                    ? "Analysis could not complete — see details below."
                    : "Analysis complete."
                  : "Detonating in sandbox… this can take a few minutes. Results refresh automatically."}
              </div>
            </div>
          )}

          {!adv.advStarted ? (
            <div style={{ padding: "14px 16px" }}>
              <p className="muted" style={{ marginTop: 0, fontSize: "0.85rem", lineHeight: 1.5 }}>
                Detonate this email's attachments and URLs in an external malware
                sandbox for deeper analysis. This runs on demand and can take
                several minutes; a malicious result escalates the verdict to
                Malicious. Use the <strong>Perform Advanced Analysis</strong>{" "}
                button next to the verdict to start.
              </p>
              {adv.error && (
                <div className="muted" style={{ marginTop: 10, color: "#f87171", fontSize: "0.82rem" }}>
                  {adv.error}
                </div>
              )}
            </div>
          ) : items.length === 0 ? (
            <div className="placeholder-row" style={{ padding: "12px 16px" }}>
              {adv.error || "Nothing was eligible for detonation."}
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Target</th>
                      {cols.map((c) => <th key={c.key}>{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i}>
                        <td style={{ wordBreak: "break-all" }}>
                          <span style={badgeStyle(it.kind)}>{it.kind === "url" ? "URL" : "FILE"}</span>
                          {it.label}
                        </td>
                        {cols.map((c) => <td key={c.key}>{cell(it.providers?.[c.key])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="muted" style={{ padding: "10px 16px", fontSize: "0.8rem", lineHeight: 1.5 }}>
                Files and URLs are detonated in external sandboxes; results refresh
                automatically and can take several minutes. A malicious detonation
                escalates the verdict above to Malicious.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Modal (choose platform) ---------------- */
export function AdvancedAnalysisModal({ adv }) {
  if (!adv.modalOpen) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => !adv.starting && adv.setModalOpen(false)}
      style={{
        position: "fixed", inset: 0, background: "rgba(2, 6, 23, 0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
      }}
    >
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "min(440px, 94vw)", padding: 22 }}>
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Choose analysis platform</h3>
        <p className="muted" style={{ fontSize: "0.85rem", lineHeight: 1.5, marginTop: 0 }}>
          More than one sandbox is configured. Select where to detonate this
          email's attachments and URLs.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "14px 0" }}>
          {adv.providers.map((p) => (
            <label key={p.key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="radio"
                name="advProvider"
                value={p.key}
                checked={adv.choice === p.key}
                onChange={() => adv.setChoice(p.key)}
              />
              <span>{p.label}</span>
            </label>
          ))}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.85rem", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={adv.setAsDefault}
            onChange={(e) => adv.setSetAsDefault(e.target.checked)}
          />
          <span>Set as the default platform for advanced analysis</span>
        </label>

        {adv.error && (
          <div className="muted" style={{ marginTop: 10, color: "#f87171", fontSize: "0.82rem" }}>{adv.error}</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button type="button" className="secondary-btn" onClick={() => adv.setModalOpen(false)} disabled={adv.starting}>
            Cancel
          </button>
          <button
            type="button"
            className="analyze-btn"
            onClick={() => adv.startAdvanced(adv.choice, { setAsDefault: adv.setAsDefault })}
            disabled={!adv.choice || adv.starting}
          >
            {adv.starting ? "Starting…" : "Run analysis"}
          </button>
        </div>
      </div>
    </div>
  );
}
